/**
 * Who is playing.
 *
 * The handoff token from chaostree is a JWT, and it already carries everything
 * the menu needs to say: a username and the display id that identifies the
 * account across the site. Reading it here saves a second round trip for
 * something the client was handed on the first one.
 *
 * ── This is not authentication ──────────────────────────────────────────────
 *
 * The signature is *not* checked, and it must not be treated as if it were.
 * Anything a client can decode a client can forge, so this is for putting a
 * name on a screen and nothing else. The game backend verifies the same token
 * against `JWT_SECRET` before it means anything — that is where identity is
 * decided, and this is only how the player learns what it decided.
 */

/**
 * The role chaostree gives an account.
 *
 * `him` is the admin role — it is what `userManager` checks for every
 * privileged operation on the site. Anything else is an ordinary player.
 */
export function isAdmin(player: PlayerIdentity | null): boolean {
  return player?.who === 'him';
}

export interface PlayerIdentity {
  uuid: string;
  /** The public handle, e.g. what `/users/<displayID>` resolves to. */
  displayID: string;
  username: string;
  /** Role, as chaostree understands it. `user` for nearly everyone. */
  who: string;
}

/**
 * Pull the claims out of a JWT without verifying it.
 *
 * Base64url, which is not what `atob` takes: it swaps two characters and drops
 * the padding, and feeding it straight in fails on roughly a quarter of real
 * tokens. Cheap to get right, and miserable to debug when it is wrong.
 */
export function readIdentity(token: string): PlayerIdentity | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Round-tripped through percent-encoding so a name with anything outside
    // Latin-1 in it survives — `atob` gives bytes, not characters.
    const json = decodeURIComponent(
      Array.from(atob(padded), (character) =>
        `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`,
      ).join(''),
    );

    const claims = JSON.parse(json) as Partial<PlayerIdentity>;
    if (!claims.uuid || !claims.displayID) return null;

    return {
      uuid: claims.uuid,
      displayID: claims.displayID,
      username: claims.username ?? claims.displayID,
      who: claims.who ?? 'user',
    };
  } catch {
    // A malformed token is a sign-in problem, and the sign-in path already
    // reports those. Here it just means there is no name to show.
    return null;
  }
}
